'use client';

import { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/app/context/AuthContext';
import { useSavedElements } from '@/app/hooks/useSavedElements';
import { ACTION_BUTTON, SAVE_BUTTON, SECONDARY_BUTTON, SHARE_BUTTON, SHARE_BUTTON_COPIED } from '@/app/components/ui/actionButtons';
import { DESIGN_TYPES, designPath, type PublicDesign } from '@/app/lib/community/shared';
import { CLIENT_ADAPTERS } from '@/app/lib/community/types';

type Notice = { tone: 'ok' | 'error'; text: string; href?: string; linkText?: string } | null;

async function post(url: string, body: unknown): Promise<{ ok: boolean; data: Record<string, unknown> }> {
  try {
    const res = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    return { ok: res.ok, data };
  } catch {
    return { ok: false, data: { error: 'Network error. Try again.' } };
  }
}

export default function DesignActions({ design, isAuthor, initialLiked, canUnpublish = true }: {
  design: PublicDesign;
  isAuthor: boolean;
  initialLiked: boolean;
  /** False when a moderator removed it or reports have it hidden (canAuthorUnpublish) — the server refuses. */
  canUnpublish?: boolean;
}) {
  const { user } = useAuth();
  const saved = useSavedElements();
  const router = useRouter();
  const info = DESIGN_TYPES[design.type];
  const adapter = CLIENT_ADAPTERS[design.type];
  const download = adapter.download?.(design);
  const path = designPath(design.type, design.id);
  const signInHref = `/login?redirect=${encodeURIComponent(path)}`;
  const live = design.status === 'approved';

  const [notice, setNotice] = useState<Notice>(null);
  const [saving, setSaving] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [liked, setLiked] = useState(initialLiked);
  const [likeCount, setLikeCount] = useState(design.likeCount);
  const [liking, setLiking] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [reporting, setReporting] = useState(false);
  const [reported, setReported] = useState(false);
  const [confirmUnpublish, setConfirmUnpublish] = useState(false);
  const [unpublishing, setUnpublishing] = useState(false);

  const saveCopy = async () => {
    setSaving(true);
    setNotice(null);
    const { error } = await adapter.saveCopy(saved, design);
    setSaving(false);
    if (error) {
      setNotice({
        tone: 'error',
        text: /already exists/i.test(error) ? `You already have a ${info.singular} called "${design.title}" in your library.` : error,
      });
      return;
    }
    setNotice({ tone: 'ok', text: 'Saved to your library.', href: '/saved', linkText: 'Open My Library' });
    // Recorded only after the save landed, so the number reflects real copies.
    void post('/api/designs/copy', { id: design.id });
  };

  // Optimistic, then settled from the server's count. The request names the state wanted, not "toggle",
  // so a double click cannot undo itself on the server.
  const toggleLike = async () => {
    const next = !liked;
    setLiking(true);
    setLiked(next);
    setLikeCount(c => Math.max(0, c + (next ? 1 : -1)));
    const { ok, data } = await post('/api/designs/like', { id: design.id, liked: next });
    setLiking(false);
    if (!ok) {
      setLiked(!next);
      setLikeCount(c => Math.max(0, c + (next ? -1 : 1)));
      setNotice({ tone: 'error', text: typeof data.error === 'string' ? data.error : 'Could not save that. Try again.' });
      return;
    }
    if (typeof data.likeCount === 'number') setLikeCount(data.likeCount);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}${path}`);
      setCopiedLink(true);
      window.setTimeout(() => setCopiedLink(false), 2000);
    } catch {
      setNotice({ tone: 'error', text: 'Could not copy the link. Copy it from the address bar instead.' });
    }
  };

  const sendReport = async () => {
    setReporting(true);
    const { ok, data } = await post('/api/designs/report', { id: design.id, reason });
    setReporting(false);
    if (!ok) {
      setNotice({ tone: 'error', text: typeof data.error === 'string' ? data.error : 'Could not send the report.' });
      return;
    }
    setReported(true);
    setReportOpen(false);
    setNotice({ tone: 'ok', text: 'Thanks — a moderator will take a look.' });
  };

  const unpublish = async () => {
    setUnpublishing(true);
    const { ok, data } = await post('/api/designs/unpublish', { id: design.id });
    setUnpublishing(false);
    if (!ok) {
      setConfirmUnpublish(false);
      setNotice({ tone: 'error', text: typeof data.error === 'string' ? data.error : 'Could not unpublish.' });
      return;
    }
    router.push('/saved?tab=published');
    router.refresh();
  };

  const heart = (
    <svg aria-hidden="true" className={`w-4 h-4 ${liked ? 'text-rose-400' : ''}`} fill={liked ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
    </svg>
  );

  return (
    <div className="space-y-3">
      {/* text-center + justify-center: a <button> centers its label by default and a link does not, so
          "Copy link" sat centered under three left-aligned buttons. */}
      <div className="flex flex-wrap gap-2 text-center">
        <Link href={adapter.openHref(design)} onClick={() => adapter.prepareOpen?.(design)} className={`${ACTION_BUTTON} ${SAVE_BUTTON}`}>
          Open in {info.editorName}
        </Link>
        {download && (
          <a href={download.href} download={download.filename} className={`${ACTION_BUTTON} ${SECONDARY_BUTTON}`}>
            {download.label}
          </a>
        )}
        {user ? (
          <button type="button" onClick={saveCopy} disabled={saving} className={`${ACTION_BUTTON} ${SECONDARY_BUTTON}`}>
            {saving ? 'Saving…' : 'Save a copy'}
          </button>
        ) : (
          <Link href={signInHref} className={`${ACTION_BUTTON} ${SECONDARY_BUTTON}`}>Sign in to save a copy</Link>
        )}
        {live && !isAuthor && (user ? (
          <button
            type="button"
            onClick={toggleLike}
            disabled={liking}
            aria-pressed={liked}
            className={`${ACTION_BUTTON} ${SECONDARY_BUTTON} inline-flex items-center justify-center gap-1.5`}
          >
            {heart}
            {liked ? 'Liked' : 'Like'}
            <span className="tabular-nums text-zinc-400">{likeCount}</span>
          </button>
        ) : (
          <Link href={signInHref} className={`${ACTION_BUTTON} ${SECONDARY_BUTTON} inline-flex items-center justify-center gap-1.5`}>
            {heart}
            Sign in to like
          </Link>
        ))}
        <button type="button" onClick={copyLink} className={`${ACTION_BUTTON} ${copiedLink ? SHARE_BUTTON_COPIED : SHARE_BUTTON}`}>
          {copiedLink ? 'Link copied' : 'Copy link'}
        </button>
      </div>

      {notice && (
        <p role="status" className={`text-sm ${notice.tone === 'ok' ? 'text-emerald-400' : 'text-red-400'}`}>
          {notice.text}{' '}
          {notice.href && <Link href={notice.href} className="underline hover:text-white">{notice.linkText}</Link>}
        </p>
      )}

      {isAuthor ? (canUnpublish && (
        <div className="pt-3 border-t border-zinc-800">
          {confirmUnpublish ? (
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="text-zinc-300">Remove this design from the community?</span>
              <button type="button" onClick={unpublish} disabled={unpublishing} className="px-3 py-1.5 rounded-lg text-sm font-medium bg-red-600/90 hover:bg-red-600 text-white disabled:opacity-60">
                {unpublishing ? 'Removing…' : 'Yes, unpublish'}
              </button>
              <button type="button" onClick={() => setConfirmUnpublish(false)} className="px-3 py-1.5 rounded-lg text-sm text-zinc-400 hover:text-white">Cancel</button>
            </div>
          ) : (
            <button type="button" onClick={() => setConfirmUnpublish(true)} className="text-sm text-zinc-400 hover:text-red-400">
              Unpublish this design
            </button>
          )}
        </div>
      )) : live && (
        <div className="pt-3 border-t border-zinc-800">
          {!user ? (
            <Link href={signInHref} className="text-sm text-zinc-500 hover:text-zinc-300">Sign in to report this design</Link>
          ) : reported ? null : reportOpen ? (
            <div className="space-y-2">
              <label htmlFor="report-reason" className="block text-sm text-zinc-300">What is wrong with this design? (optional)</label>
              <textarea
                id="report-reason"
                value={reason}
                onChange={e => setReason(e.target.value.slice(0, 500))}
                rows={3}
                className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-200 focus:outline-none focus:border-emerald-500"
              />
              <div className="flex gap-2">
                <button type="button" onClick={sendReport} disabled={reporting} className="px-3 py-1.5 rounded-lg text-sm font-medium bg-zinc-800 hover:bg-zinc-700 text-zinc-100 disabled:opacity-60">
                  {reporting ? 'Sending…' : 'Send report'}
                </button>
                <button type="button" onClick={() => setReportOpen(false)} className="px-3 py-1.5 rounded-lg text-sm text-zinc-400 hover:text-white">Cancel</button>
              </div>
            </div>
          ) : (
            <button type="button" onClick={() => setReportOpen(true)} className="text-sm text-zinc-500 hover:text-zinc-300">
              Report this design
            </button>
          )}
        </div>
      )}
    </div>
  );
}
